import { Box, Divider, Grid, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BriaButton from "../../../../../components/common/BriaButton/BriaButton";
import BriaDropdown, { DropDownItem } from "../../../../../components/common/BriaDropDown/BriaDropDown";
import BriaInput from "../../../../../components/common/BriaInput/BriaInput";
import BriaSlider from "../../../../../components/common/BriaSlider/BriaSlider";
import LoadingPlaceholder from "../../../../../components/common/LoadingPlaceholder/LoadingPlaceholder";
import { useAppStore } from "../../../../../hooks/useStores";
import InputLayout from "../../../../../layout/InputLayout/InputLayout";
import { formatScientific } from "../../../../../utils";
import {
	CheckpointInterval,
	LEARNING_RATE_CONFIG,
	LEARNING_RATE_VALUES,
	Rank,
	TOTAL_TRAINING_STEPS_CONFIG,
	TgModelLearningRateScheduler,
	TgModelParameters,
	TgTrainingVersion,
} from "../../store/tg-model-store";
import styles from "./TgExpertParametersSetup.module.scss";

interface TgExpertParametersSetupProps {
	handleCreateModelAndStartTraining: (datasetName?: string) => void;
	loading?: boolean;
	datasetSize?: number;
}

const TgExpertParametersSetup = ({
	handleCreateModelAndStartTraining,
	loading = false,
	datasetSize,
}: TgExpertParametersSetupProps) => {
	const { t } = useTranslation("tg", { keyPrefix: "models.create.expertParameters" });
	const { t: t2 } = useTranslation("tg", { keyPrefix: "models.parametersLrScheduler" });
	const { tgStore } = useAppStore();
	const { tgModelStore } = tgStore;

	// Form state
	const [modelName, setModelName] = useState("");
	const [modelDescription, setModelDescription] = useState("");
	const [briaModel, setBriaModel] = useState<TgTrainingVersion>("bria-3.2");
	const [rank, setRank] = useState<Rank>(Rank.RANK_32);
	const snapLearningRate = (value: number) => {
		return LEARNING_RATE_VALUES.reduce(
			(prev, curr) => (Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev),
			LEARNING_RATE_VALUES[0],
		);
	};
	const [learningRate, setLearningRate] = useState<number>(LEARNING_RATE_VALUES[2]);
	const [learningRateScheduler, setLearningRateScheduler] = useState<TgModelLearningRateScheduler>(
		TgModelLearningRateScheduler.LINEAR,
	);
	const [checkpointInterval, setCheckpointInterval] = useState<CheckpointInterval | undefined>(undefined);
	const [totalTrainingSteps, setTotalTrainingSteps] = useState<number>(500);

	const [loadingDefaults, setLoadingDefaults] = useState(true);

	useEffect(() => {
		const loadDefaults = async () => {
			if (tgModelStore.modelForm.training_parameters != undefined) {
				// Incase of duplicate action clicked, these values will be prefilled.
				setRank(tgModelStore.modelForm.training_parameters.rank);
				setLearningRate(snapLearningRate(tgModelStore.modelForm.training_parameters.learning_rate));
				setTotalTrainingSteps(tgModelStore.modelForm.training_parameters.total_training_steps);
				setLearningRateScheduler(tgModelStore.modelForm.training_parameters.lr_scheduler as TgModelLearningRateScheduler);
				setCheckpointInterval(tgModelStore.modelForm.training_parameters.checkpoint_interval);
				setBriaModel(tgModelStore.modelForm.training_version);
				setModelName(tgModelStore.modelForm.name);
				setModelDescription(tgModelStore.modelForm.description);
				setLoadingDefaults(false);
				return;
			}


			try {
				setLoadingDefaults(true);
				const { ip_medium, ip_type } = tgStore.tgProjectStore.projectToView;
				if (!ip_medium || !ip_type) return;
				await tgModelStore.fetchDefaultExpertParameters({
					ip_medium,
					ip_type,
					dataset_size: datasetSize ?? 0,
				});
				const d = tgModelStore.defaultExpertParameters;
				if (d) {
					setRank(d.rank);
					setLearningRate(snapLearningRate(d.learning_rate));
					setTotalTrainingSteps(d.total_training_steps);
					setLearningRateScheduler(d.lr_scheduler as TgModelLearningRateScheduler);
				}
			} finally {
				setLoadingDefaults(false);
			}
		};
		loadDefaults();
	}, [datasetSize]);

	const briaModelItems: DropDownItem<TgTrainingVersion>[] = [
		{
			key: "BRIA 3.2",
			value: "bria-3.2",
		},
		{
			key: "BRIA 2.3",
			value: "bria-2.3",
		},
	];

	const rankItems: DropDownItem<Rank>[] = [
		{ key: Rank.RANK_4.toString(), value: Rank.RANK_4 },
		{ key: Rank.RANK_8.toString(), value: Rank.RANK_8 },
		{ key: Rank.RANK_16.toString(), value: Rank.RANK_16 },
		{ key: Rank.RANK_32.toString(), value: Rank.RANK_32 },
		{ key: Rank.RANK_64.toString(), value: Rank.RANK_64 },
		{ key: Rank.RANK_128.toString(), value: Rank.RANK_128 },
		{ key: Rank.RANK_256.toString(), value: Rank.RANK_256 },
	];

	const learningRateSchedulerItems: DropDownItem<TgModelLearningRateScheduler>[] = [
		{ key: t2("linear"), value: TgModelLearningRateScheduler.LINEAR },
		{ key: t2("cosine"), value: TgModelLearningRateScheduler.COSINE },
		{ key: t2("cosine_with_restarts"), value: TgModelLearningRateScheduler.COSINE_WITH_RESTARTS },
		{ key: t2("polynomial"), value: TgModelLearningRateScheduler.POLYNOMIAL },
		{ key: t2("constant"), value: TgModelLearningRateScheduler.CONSTANT },
		{ key: t2("constant_with_warmup"), value: TgModelLearningRateScheduler.CONSTANT_WITH_WARMUP },
	];

	// Filter checkpoint interval options based on total training steps
	const getCheckpointIntervalItems = (): DropDownItem<CheckpointInterval>[] => {
		const intervals = [
			CheckpointInterval.INTERVAL_100,
			CheckpointInterval.INTERVAL_200,
			CheckpointInterval.INTERVAL_500,
			CheckpointInterval.INTERVAL_1000,
			CheckpointInterval.INTERVAL_1500,
		];

		return intervals
			.filter((interval) => interval <= totalTrainingSteps)
			.map((interval) => ({
				key: interval.toString(),
				value: interval,
			}));
	};

	// Update checkpoint interval if current value is larger than total training steps
	useEffect(() => {
		if (!checkpointInterval) return;
		const availableIntervals = getCheckpointIntervalItems();
		if (checkpointInterval > totalTrainingSteps && availableIntervals.length > 0) {
			setCheckpointInterval(availableIntervals[availableIntervals.length - 1].value);
		}
	}, [totalTrainingSteps, checkpointInterval]);

	// Update model form when parameters change
	useEffect(() => {
		const parameters: TgModelParameters = {
			rank,
			checkpoint_interval: checkpointInterval,
			lr_scheduler: learningRateScheduler,
			learning_rate: learningRate,
			total_training_steps: totalTrainingSteps,
		};

		tgModelStore.handleFormChange("name", modelName);
		tgModelStore.handleFormChange("description", modelDescription);
		tgModelStore.handleFormChange("training_version", briaModel);
		tgModelStore.handleFormChange("training_parameters", parameters);
	}, [
		modelName,
		modelDescription,
		briaModel,
		rank,
		learningRate,
		learningRateScheduler,
		checkpointInterval,
		totalTrainingSteps,
		tgModelStore,
	]);

	const handleStartTraining = () => {
		handleCreateModelAndStartTraining();
	};

	const isFormValid = modelName.trim().length > 0;

	const generateMarks = (start: number, end: number, totalPoints: number = 6) => {
		const step = (end - start) / (totalPoints - 1);
		const marks = [];

		for (let i = 0; i < totalPoints; i++) {
			const value = Math.round(start + step * i);
			marks.push({
				value,
				label: i === 0 || i === totalPoints - 1 ? String(value) : "",
			});
		}

		return marks;
	};

	return (
		<LoadingPlaceholder isLoading={loadingDefaults} className={styles.loadingPlaceholder}>
			<Box className={styles.container}>
				<Typography className={styles.title}>{t("title")}</Typography>
				<Typography className={styles.subtitle}>{t("subtitle")}</Typography>
				<Divider className={styles.divider} />

				<Box className={styles.formContainer}>
					<Grid container spacing={3} className={styles.formGrid}>
						<Grid item xs={12} md={3}>
							<InputLayout
								label={t("modelName.label")}
								info={t("modelName.tooltip")}
								className={styles.inputLayout}
							>
								<BriaInput
									value={modelName}
									onChange={(e) => setModelName(e.target.value)}
									placeholder={t("modelName.placeholder")}
									className={styles.input}
								/>
							</InputLayout>
						</Grid>
						<Grid item xs={12} md={9}>
							<InputLayout label={t("modelDescription.label")} className={styles.inputLayout}>
								<BriaInput
									value={modelDescription}
									onChange={(e) => setModelDescription(e.target.value)}
									placeholder={t("modelDescription.placeholder")}
									className={styles.input}
								/>
							</InputLayout>
						</Grid>

						<Grid item xs={12} md={4}>
							<InputLayout
								label={t("baseModel.label")}
								info={t("baseModel.tooltip")}
								className={styles.inputLayout}
							>
								<BriaDropdown
									value={briaModel}
									onChange={(e) => setBriaModel(e.target.value as TgTrainingVersion)}
									items={briaModelItems}
								/>
							</InputLayout>
						</Grid>
						<Grid item xs={12} md={4}>
							<InputLayout
								label={t("rank.label")}
								info={t("rank.tooltip")}
								className={styles.inputLayout}
							>
								<BriaDropdown
									value={rank}
									onChange={(e) => setRank(e.target.value as Rank)}
									items={rankItems}
								/>
							</InputLayout>
						</Grid>
						<Grid item xs={12} md={4}>
							<InputLayout
								label={t("learningRate.label")}
								info={t("learningRate.tooltip")}
								className={styles.inputLayout}
							>
								<BriaSlider
									value={learningRate}
									onChange={(_, value) => setLearningRate(snapLearningRate(value as number))}
									min={LEARNING_RATE_CONFIG.min}
									max={LEARNING_RATE_CONFIG.max}
									step={null}
									showOnlyEdgeMarkLabels
									className={styles.slider}
									valueLabelDisplay={"auto"}
									valueLabelFormat={(v) => formatScientific(v as number)}
									showMarksPoints
									labelsAlwaysGray
									logScale
									logBase={10}
									marks={LEARNING_RATE_VALUES.map((v) => ({ value: v, label: formatScientific(v) }))}
								/>
							</InputLayout>
						</Grid>

						<Grid item xs={12} md={4}>
							<InputLayout
								label={t("totalTrainingSteps.label")}
								info={t("totalTrainingSteps.tooltip")}
								className={styles.inputLayout}
							>
								<BriaSlider
									value={totalTrainingSteps}
									onChange={(_, value) => setTotalTrainingSteps(value as number)}
									min={TOTAL_TRAINING_STEPS_CONFIG.min}
									max={TOTAL_TRAINING_STEPS_CONFIG.max}
									step={TOTAL_TRAINING_STEPS_CONFIG.step}
									className={styles.slider}
									valueLabelDisplay={"on"}
									showMarksPoints
									labelsAlwaysGray
									marks={generateMarks(
										TOTAL_TRAINING_STEPS_CONFIG.min,
										TOTAL_TRAINING_STEPS_CONFIG.max,
									)}
								/>
							</InputLayout>
						</Grid>
						<Grid item xs={12} md={4}>
							<InputLayout
								label={t("learningRateScheduler.label")}
								info={t("learningRateScheduler.tooltip")}
								className={styles.inputLayout}
							>
								<BriaDropdown
									value={learningRateScheduler}
									onChange={(e) =>
										setLearningRateScheduler(e.target.value as TgModelLearningRateScheduler)
									}
									items={learningRateSchedulerItems}
								/>
							</InputLayout>
						</Grid>
						<Grid item xs={12} md={4}>
							<InputLayout
								label={t("checkpointInterval.label")}
								info={t("checkpointInterval.tooltip")}
								className={styles.inputLayout}
							>
								<BriaDropdown
									value={checkpointInterval}
									onChange={(e) => setCheckpointInterval(e.target.value as CheckpointInterval)}
									items={getCheckpointIntervalItems()}
									placeholder={t("checkpointInterval.placeholder")}
									renderValue={(selected) => {
										if (!selected) return t("checkpointInterval.placeholder");
										return selected;
									}}
								/>
							</InputLayout>
						</Grid>
					</Grid>
				</Box>

				<Divider className={styles.divider} />

				<Box className={styles.buttonContainer}>
					<BriaButton
						buttonType="primaryMedium"
						onClick={handleStartTraining}
						disabled={!isFormValid || loading}
						loading={loading}
						className={styles.trainButton}
					>
						{t("startTrainingButton")}
					</BriaButton>
				</Box>
			</Box>
		</LoadingPlaceholder>
	);
};

export default observer(TgExpertParametersSetup);
